// =============================================================================
// Who may edit My Blocks (/farm/blocks) — the page's gate, written to match the
// database, not a guess at it.
//
// Every registry write on the page lands on a table whose RLS has ONE shape —
// blocks_write_scoped, pucs_write_scoped, puc_farms_write_scoped, growers_write
// and block_year_status_write_* all read (checked on prod, 1 Oct 2026):
//
//   is_platform_admin()
//   OR ( current_tenant_role() IN (ADMIN, MANAGER, FARM_ADMIN, FARM_MANAGER)
//        AND the row's grower has tenant_id = current_tenant_id() )
//
// The page used to gate on isManagerOrAbove(me.role), which disagrees with that
// three ways, and each one handed an operator an edit control whose save came
// back "Update to blocks was blocked" (svsfarm, four times, 9–21 Sep):
//
//   (a) Support view. The JWT hook's support branch mints no tenant_role, and
//       is_platform_admin() is FALSE inside a support view, so every save is
//       refused. Jason ruled 1 Oct that a support view is read-only anyway.
//   (b) SUPER_ADMIN. me.role keeps SUPER_ADMIN sticky, but the policy never
//       names it: it passes only as a platform admin in platform mode, or by
//       holding one of the four roles in THIS tenant.
//   (c) A registry the farm does not own. A packhouse-managed grower row has
//       the packhouse's tenant_id, so the farm can read it and never write it.
//
// Plantings (farm_projects) are NOT this shape: their RLS is tenant-only, so a
// support-view save would SUCCEED there. The page's manager rule stays, and the
// support view is taken out here — on that table this gate is the only barrier.
// =============================================================================

import type { UserRole } from '@/types/database'
import { isManagerOrAbove } from '@/lib/roles'

/** The roles the registry policies name. Not SUPER_ADMIN — see (b) above. */
export const REGISTRY_EDIT_ROLES: readonly UserRole[] = ['ADMIN', 'MANAGER', 'FARM_ADMIN', 'FARM_MANAGER']

/** Who is asking, in the terms the policy reads. */
export interface RegistryEditor {
  /** What current_tenant_role() returns for this session — use tenantRoleOf(me). */
  tenantRole:   UserRole | null | undefined
  /** In platform mode (the platform_view claim) — is_platform_admin() is TRUE. */
  platformMode: boolean
  /** In a support view (the support_view claim) — read-only by ruling. */
  supportView:  boolean
}

// tenantRoleOf lives in lib/roles (shared code asks it too — the piece-rates
// page, payroll, Ask); re-exported here for the farm callers that import it
// from this file.
export { tenantRoleOf } from '@/lib/roles'

/** The "who" half of the policy: may this session write registry rows at all?
 *  Also the gate for creating the master grower row, which is this farm's by
 *  construction (the insert stamps tenant_id = the active tenant). */
export function registryEditorAllowed(e: RegistryEditor): boolean {
  if (e.supportView) return false
  if (e.platformMode) return true
  return !!e.tenantRole && REGISTRY_EDIT_ROLES.includes(e.tenantRole)
}

/** Both halves: the session may write, AND the farm owns the registry
 *  (its grower row's tenant_id is the active tenant). */
export function canEditFarmRegistry(e: RegistryEditor & { farmOwned: boolean }): boolean {
  return e.farmOwned && registryEditorAllowed(e)
}

/** Plantings on a pivot/field block: the page's manager rule, never in a
 *  support view (farm_projects RLS would accept that write — see above). */
export function canEditPlantings(p: { role: UserRole | null | undefined; supportView: boolean }): boolean {
  return !p.supportView && isManagerOrAbove(p.role)
}

/** Who may DELETE (or restore) a planting: the database says so since mig
 *  20261280000000 (farm_projects_delete_manager_gate) — a manager seat in THIS
 *  tenant (current_tenant_role: ADMIN, MANAGER, SUPER_ADMIN, FARM_ADMIN,
 *  FARM_MANAGER) or a platform admin. Pass the TENANT role — tenantRoleOf(me) —
 *  not me.role: a user who is ADMIN on another farm and FARM_WORKER here would
 *  otherwise see a Delete the server refuses. Editing a planting stays
 *  canEditPlantings (tenant-only RLS); only delete / restore is manager-only. */
export function canDeletePlantings(e: { tenantRole: UserRole | null | undefined; platformMode: boolean; supportView: boolean }): boolean {
  return !e.supportView && (e.platformMode || isManagerOrAbove(e.tenantRole))
}

export const SUPPORT_VIEW_READ_ONLY = 'Support view is read-only — the farm’s own admins make changes.'
export const PACKHOUSE_MANAGED_READ_ONLY =
  'Your packhouse manages this register, so it is read-only here — ask the packhouse to change the farm details, PUCs, farms or blocks.'

/** One line saying why someone who would expect to edit sees no edit controls,
 *  or null. Staff below manager get none: read-only is simply their role. */
export function registryReadOnlyReason(e: RegistryEditor & { farmOwned: boolean }): string | null {
  if (e.supportView) return SUPPORT_VIEW_READ_ONLY
  if (!e.farmOwned && registryEditorAllowed(e)) return PACKHOUSE_MANAGED_READ_ONLY
  return null
}

/** What the operator reads when a save is still refused — the gate now matches
 *  the policy, so this is a role that changed after the page loaded. */
export const REGISTRY_WRITE_REFUSED =
  'Nothing was saved — your access to this farm’s blocks has changed since the page loaded. Refresh the page; if you can still edit and this happens again, ask the farm’s admin to check your role.'

/** friendlyError domain for the page: updateWithVersion's WriteBlockedError
 *  (an RLS UPDATE-deny) otherwise reaches the screen as its raw text. The
 *  error itself still reaches Sentry unchanged — a refusal the gate did not
 *  predict is worth seeing. Pinned against a real WriteBlockedError in the
 *  sibling test, so a reworded message cannot silently stop matching. */
export const REGISTRY_WRITE_DOMAIN: [RegExp, string][] = [
  [/^Update to \w+ was blocked\b/, REGISTRY_WRITE_REFUSED],
]
